import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import {
  deleteCluster,
  editCluster,
  getCluster,
} from 'services/clusterService';
import { RootState } from 'redux-setup/store';
import { useAppSelector } from 'hooks/reduxHooks';
import { toast } from 'react-toastify';
import { IGetClusterResponse } from 'interface/clusterInterface';
import { Loader } from 'components/common/loader';
import { Path } from 'router/constant';
import './styles.scss';

export const ClusterDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cluster, setCluster] = useState<IGetClusterResponse>({
    ID: '',
    Name: '',
    Version: 0,
    Server: '',
    CreatedAt: '',
    UpdatedAt: '',
  });
  const [loading, setLoading] = useState(true);
  const organizationID = useAppSelector(
    (state: RootState) => state.organization.id,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');

  const fetchClusterDetails = async () => {
    setLoading(true);
    if (!id || !organizationID) {
      toast.error('Failed to fetch cluster details');
      return;
    }

    const payload = {
      ID: id,
      OrganizationID: organizationID,
    };

    const response = await getCluster(payload);
    const err = response.check();
    if (err) {
      toast.error('Failed to fetch cluster details');
      return;
    }

    const cluster = response.unwrap();
    if (cluster.ID) {
      setCluster(cluster);
      setLoading(false);
    } else {
      toast.error('Failed to fetch cluster details');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClusterDetails();
  }, [id, organizationID]);

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedName(cluster.Name);
  };

  const handleSave = async () => {
    if (!id || !organizationID) {
      toast.error('Failed to save cluster details');
      return;
    }

    const clusterID = id.toString();

    const payload = {
      Name: editedName,
      ID: clusterID,
      OrganizationID: organizationID,
      Version: cluster.Version,
    };

    const response = await editCluster(payload);
    const err = response.check();
    if (err) {
      toast.error('Failed to save cluster details');
      return;
    }

    const clusterResponse = response.unwrap();
    if (clusterResponse.ID) {
      setIsEditing(false);
      await fetchClusterDetails();
    } else {
      toast.error('Failed to save cluster details');
    }
  };

  const handleDelete = async () => {
    if (!id || !organizationID) {
      toast.error('Failed to delete cluster');
      return;
    }

    const payload = {
      ID: id,
      OrganizationID: organizationID,
      Version: cluster.Version,
    };
    const response = await deleteCluster(payload);

    const err = response.check();
    if (err) {
      toast.error('Failed to delete cluster');
      return;
    }

    const clusterResponse = response.unwrap();
    if (clusterResponse.ID) {
      toast.success('Cluster deleted successfully');
      navigate(Path.CLUSTERS);
    } else {
      toast.error('Failed to delete cluster');
    }
  };

  if (loading) {
    return <Loader loading={loading} />;
  }

  return (
    <Box p={3}>
      <div className="cluster-details">
        <div className="cluster-details-item">
          <div className="flex justify-between items-center">
            <Typography variant="h6" gutterBottom>
              Cluster Details
            </Typography>
            <div className="flex gap-2">
              {isEditing ? (
                <Button variant="contained" onClick={handleSave}>
                  Save
                </Button>
              ) : (
                <Button variant="outlined" onClick={handleEditClick}>
                  Edit
                </Button>
              )}
              <Button variant="contained" onClick={() => handleDelete()}>
                Delete
              </Button>
            </div>
          </div>
          <div className="details-grid">
            <div className="detail-item">
              <Typography className="detail-label">Name</Typography>
              {isEditing ? (
                <input
                  type="text"
                  value={editedName}
                  onChange={e => setEditedName(e.target.value)}
                  className="p-2 border rounded"
                />
              ) : (
                <Typography className="detail-value">{cluster.Name}</Typography>
              )}
            </div>

            <div className="detail-item">
              <Typography className="detail-label">Version</Typography>
              <Typography className="detail-value">
                {cluster.Version}
              </Typography>
            </div>

            <div className="detail-item">
              <Typography className="detail-label">Server</Typography>
              <Typography className="detail-value">{cluster.Server}</Typography>
            </div>

            <div className="detail-item">
              <Typography className="detail-label">Created At</Typography>
              <Typography className="detail-value">
                {new Date(cluster.CreatedAt).toLocaleString()}
              </Typography>
            </div>

            <div className="detail-item">
              <Typography className="detail-label">Updated At</Typography>
              <Typography className="detail-value">
                {new Date(cluster.UpdatedAt).toLocaleString()}
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};
